import { PayoutRuleType } from '@common-fe/common-fe';

import { IRenewalHealthPlanDto } from '@/dtos';
import {
  IAutoFundingConfigRenewalDto,
  ICategorySpendLimitRenewalDto,
  IDeductibleConfigRenewalDto,
  IPaymentRuleRenewalDto,
  IProcessingTierRenewalDto,
  IRenewalPayrollCalendarDto,
  IRenewalPayrollCalendarScheduleConfigDto,
  IServiceConfigRenewalDto,
  ITriggerConfigRenewalDto,
} from '@/dtos';
import { AddressesDtoPayload } from '@/modules/employer/types';
import { AddressPayload } from '@/modules/ExpressSetup/EditAddressModal/stores/useAddress.store.ts';
import { Copay, CopayDto } from '@/modules/ExpressSetup/ExpressSetup.types.ts';
import { Plan } from '@/modules/plan/plan.types.ts';

export type AutoFundingConfigRenewalDto = IAutoFundingConfigRenewalDto;
export type CategorySpendLimitRenewalDto = ICategorySpendLimitRenewalDto;
export type DeductibleConfigRenewalDto = IDeductibleConfigRenewalDto;
export type PaymentRuleRenewalDto = IPaymentRuleRenewalDto;
export type ProcessingTierRenewalDto = IProcessingTierRenewalDto;
export type RenewalHealthPlanDto = IRenewalHealthPlanDto;
export type ServiceConfigRenewalDto = IServiceConfigRenewalDto;
export type TriggerConfigRenewalDto = ITriggerConfigRenewalDto;

// --- Health Plan Renewal domain types ---

export interface RenewalPaymentRule {
  planId?: string;
  planName?: string;
  maxAmount?: number;
  percentage?: number;
}

export interface RenewalServiceItem {
  id?: number;
  name?: string;
}

export interface RenewalServiceConfig {
  serviceName?: string;
  services?: RenewalServiceItem[];
  claimValue?: number;
  isAppliedDeductible?: boolean;
  paymentType?: string;
  paymentRules?: RenewalPaymentRule[];
}

export interface RenewalProcessingTier {
  name?: string;
  displayName?: string;
  nextTierName?: string;
  payoutRuleType?: PayoutRuleType;
  cardPurseLockPlanNames?: string[];
  serviceConfigs?: RenewalServiceConfig[];
  tierTriggerIds?: number[];
}

export interface RenewalTriggerConfig {
  triggerCategoryType?: string;
  triggerName?: string;
  thresholdAmount?: number;
  triggerId?: string;
}

export interface RenewalAutoFundingConfig {
  planId?: string;
  planName?: string;
  autoEnrollmentAmount?: number;
  perPersonSpendLimitAmount?: number;
  minElectionAmount?: number;
  maxElectionAmount?: number;
}

export interface RenewalCategorySpendLimit {
  planId?: string;
  categoryType?: string;
  categorySpendLimit?: number;
}

export interface RenewalDeductibleConfig {
  coverageId?: string;
  coverageName?: string;
  coverageCode?: string;
  triggerConfigs?: RenewalTriggerConfig[];
  autoFundingConfigs?: RenewalAutoFundingConfig[];
  categorySpendLimits?: RenewalCategorySpendLimit[];
}

export interface HealthPlanRenewal {
  id?: string;
  name?: string;
  planCode?: string;
  defaultName?: string;
  defaultPlanCode?: string;
  processingTiers?: RenewalProcessingTier[];
  deductibleConfigs?: RenewalDeductibleConfig[];
  rawDto?: RenewalHealthPlanDto;
}

export enum RenewalStatus {
  NEW = 'NEW',
  CANCELED = 'CANCELED',
  REQUESTED = 'REQUESTED',
  ACTIVE = 'ACTIVE',
  PROCESSING = 'PROCESSING',
  COMPLETE = 'COMPLETE',
  RESOLVED = 'RESOLVED',
}

export enum AllowRenewalsType {
  NO_RENEWALS = 'NO_RENEWALS',
  POSTPONED = 'POSTPONED',
  BY_REQUEST = 'BY_REQUEST',
}

export interface RenewalPlanYearDto {
  name: string;
  valid_from: string;
  valid_to: string | null;
  prior_plan_year_id?: number;
}

export interface RenewalPlanDto {
  prior_plan_id: number;
  account_type: string;
  name: string;
  parent_id: number;
  is_custom_prefund_calc?: boolean;
  is_replenished?: boolean;
  is_plan_level_funding?: boolean;
  is_carded?: boolean;
  is_rollover?: boolean;
  grace_period_type?: string;
  run_out_type?: string;
  allow_investments?: boolean;
  max_rollover_amount_type?: string;
  rollover_claims_type?: string;
  max_rollover_amount?: number;
  min_rollover_amount?: number;
  days_after_plan_end?: number;
  rollover_claims_days_amount?: number;
  is_auto_enrollment?: boolean;
  plan_code?: string;
}

export type PayrollCalendarDto = IRenewalPayrollCalendarDto;
export type PayrollCalendarConfigDto = IRenewalPayrollCalendarScheduleConfigDto;

export interface PayrollCalendar {
  id: string;
  rawDto: PayrollCalendarDto;
  groupType?: string;
  calendarPlanCodes?: string[];
  name?: string;
  frequencyType?: string;
  config?: PayrollCalendarConfig;
  schedule?: string[];
}

export interface RenewalCalendarEditPayload {
  name?: string;
  config?: PayrollCalendarConfig;
  schedule?: string[];
}

export interface PayrollCalendarConfig {
  calendarId?: number;
  startDate?: string;
  frequency?: string;
  dayOfWeek?: string;
  firstDayOfMonth?: number;
  secondDayOfMonth?: number;
  month?: string;
  dayOfMonth?: number;
}

export interface RenewalDto {
  id: number;
  addresses?: AddressesDtoPayload[];
  org_id: number;
  org_name: string;
  org_path: string;
  allow_renewals_type: AllowRenewalsType;
  status: RenewalStatus;
  plan_year: RenewalPlanYearDto;
  plan_year_id: number;
  plans?: RenewalPlanDto[];
  partner_name?: string;
  copays?: CopayDto[];
  new_plan_year?: RenewalPlanYearDto;
  dark_large_logo_path?: string;
  privacy_policy_link?: string;
  terms_and_conditions_link?: string;
  pricing_consent_link?: string;
  plan_renewal_note?: string;
  health_plans?: RenewalHealthPlanDto[];
  payroll_calendars?: PayrollCalendarDto[];
}

export interface RenewalPlanYear {
  name: string;
  validFrom: string;
  validTo: string | null;
  priorPlanYearId?: number;
}

export interface Renewal {
  id: string;
  address?: AddressPayload;
  orgId: string;
  organizationName: string;
  orgPath: string;
  allowRenewalsType: AllowRenewalsType;
  status: RenewalStatus;
  planYear: RenewalPlanYear;
  planYearId: string;
  plans?: Plan[];
  plansDto?: RenewalPlanDto[];
  partnerName?: string;
  copays?: Copay[];
  newPlanYear?: RenewalPlanYear;
  newPlanYearDto?: RenewalPlanYearDto;
  logo?: string;
  privacyPolicyLink?: string;
  termsAndConditionsLink?: string;
  pricingConsentLink?: string;
  planRenewalNote?: string;
  hasRolloverPlans?: boolean;
  healthPlans?: HealthPlanRenewal[];
  payrollCalendars?: PayrollCalendar[];
}
